import { DatePicker, Dialog, LoadMoreButton, Select } from "@rio-cloud/rio-uikit";
import { useEffect, useState } from "react";
import { Moment } from "moment";
import { DtcAlert, fetchDtcAlertsHistory } from "../../../api/fetchDtcModules";
import useAssets from "../../../hooks/useAssets";
import { getAssetIdByChassis } from "../../../utils/assetUtils";

export const DtcAlertsHistoryDialog = ({ chassis }: { chassis: string }) => {
    const { allAssets } = useAssets();
    const [assetId, setAssetId] = useState<string | null>(null);

    const [showDialog, setShowDialog] = useState(false);
    const [dtcAlerts, setDtcAlerts] = useState<DtcAlert[]>([]);

    const [startAt, setStartAt] = useState<Date | null>(null);
    const [endAt, setEndAt] = useState<Date | null>(null);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState<'all' | 'attention' | 'critical'>('all');

    const [loading, setLoading] = useState(false);

    const [expandedRows, setExpandedRows] = useState<string[]>([]);

    const getStatusBadge = (status: string) => {
        const statusConfig = {
            danger: { class: 'bg-red-100 text-red-800', label: 'Alerta Crítico' },
            warning: { class: 'bg-orange-100 text-orange-800', label: 'Alerta Atenção' },
        };

        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.warning;

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-size-10 font-medium ${config.class}`}>
                {config.label}
            </span>
        );
    };

    const getStatusClass = (status: string) => {
        let className = '';
        switch (status) {
            case 'AMBER':
                className = 'warning';
                break;
            case 'MIL':
                className = 'warning';
                break;
            case 'RED':
                className = 'danger';
                break;
            case 'PROTECTED':
                className = 'danger';
                break;
            default:
                className = '';
                break;
        }

        return className;
    };

    const fetchDtcAlertsData = async (assetId: string, page: number) => {
        setLoading(true);
        if (assetId) {
            const dtcAlerts = await fetchDtcAlertsHistory([assetId], startAt ?? undefined, endAt ?? undefined, page, search, status);
            setDtcAlerts(page === 0 ? dtcAlerts.alerts : [...dtcAlerts.alerts, ...dtcAlerts.alerts]);
            setTotal(dtcAlerts.total);
            setPage(page);
            setLoading(false);
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            if (chassis) {
                setDtcAlerts([]);
                const assetId = getAssetIdByChassis(allAssets, chassis);

                if (assetId) {
                    setAssetId(assetId);
                    fetchDtcAlertsData(assetId, 0);
                }
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (assetId) {
            fetchDtcAlertsData(assetId, 0);
        }
    }, [startAt, endAt, search, status]);

    const loadMore = () => {
        if (assetId) {
            fetchDtcAlertsData(assetId, page + 1);
        }
    }

    const DtcAlertsSkeleton = () => {
        return (
            new Array(5).fill(0).map((_, index) => (
                <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                        <div className="animate-pulse">
                            <div className="w-24 h-4 bg-gray-300 rounded" />
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                        <div className="animate-pulse">
                            <div className="w-16 h-4 bg-gray-300 rounded" />
                        </div>
                    </td>
                    <td className="px-6 py-4">
                        <div className="animate-pulse">
                            <div className="w-48 h-4 bg-gray-300 rounded" />
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                        <div className="animate-pulse">
                            <div className="w-16 h-4 bg-gray-300 rounded" />
                        </div>
                    </td>
                    <td className="px-6 py-4">
                        <div className="animate-pulse">
                            <div className="w-40 h-4 bg-gray-300 rounded" />
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                        <div className="animate-pulse">
                            <div className="w-20 h-6 bg-gray-300 rounded-full" />
                        </div>
                    </td>
                    <td className='table-action' />
                </tr>
            ))
        );
    };

    return <>
        {assetId && <div
            className="cursor-pointer text-color-primary text-size-12 text-medium py-4"
            onClick={() => setShowDialog(true)}
        >
            Histórico de Alertas DTC
        </div>}
        <Dialog
            title="Histórico de Alertas DTC"
            showCloseButton
            onClose={() => setShowDialog(false)}
            onEsc={() => setShowDialog(false)}
            show={showDialog}
            bsSize={Dialog.SIZE_LG}
            body={
                <div className="p-6">
                    <div className="table-toolbar-container">
                        <div className="table-toolbar-group-left min-width-200 flex flex-row">
                            <div className="table-toolbar-column width-350 table-toolbar-column-spacer">
                                <div className="table-toolbar-search input-group false">
                                    <span className="input-group-addon"><span className="rioglyph rioglyph-search"></span></span>
                                    <div className="ClearableInput input-group">
                                        <input placeholder="Busque" className="form-control" type="text" value={search} onChange={(e) => setSearch(e.target.value)} />
                                        <span className="clearButton hide"><span className="clearButtonIcon rioglyph rioglyph-remove-sign"></span>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="table-toolbar-column display-flex align-items-center justify-content-center">
                                <div className="display-flex gap-5 justify-content-start min-width-500 px-2">
                                    <div className="width-30pct">
                                        <DatePicker
                                            id="startDate"
                                            inputProps={{ placeholder: "Selecione a data inicial" }}
                                            dateFormat="DD-MM-YYYY"
                                            timeFormat={false}
                                            onChange={(result: Moment | string) => setStartAt(result as unknown as Date)}
                                        />
                                    </div>
                                    <div className="width-30pct">
                                        <DatePicker
                                            id="endDate"
                                            inputProps={{ placeholder: "Selecione a data final" }}
                                            dateFormat="DD-MM-YYYY"
                                            timeFormat={false}
                                            onChange={(result: Moment | string) =>
                                                setEndAt(result as unknown as Date)
                                            }
                                        />
                                    </div>

                                    <div className="width-60pct">
                                        <Select
                                            id="sort-type"
                                            options={[
                                                { id: "all", label: <span><span className="rioglyph rioglyph-warning-sign text-info mr-2"></span>Todos os alertas</span>, selected: true },
                                                { id: "attention", label: <span><span className="rioglyph rioglyph-warning-sign text-warning mr-2"></span>Alertas atenção</span>, selected: false },
                                                { id: "critical", label: <span><span className="rioglyph rioglyph-warning-sign text-danger mr-2"></span>Alertas críticos</span>, selected: false },
                                            ]}
                                            value={[status]}
                                            onChange={(selected) => setStatus(selected?.id as 'all' | 'attention' | 'critical' ?? 'all')}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="table table-layout-fixed table-bordered min-w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left font-medium text-gray-500">
                                            Data/Hora
                                        </th>
                                        <th className="px-6 py-3 text-left font-medium text-gray-500">
                                            SPN
                                        </th>
                                        <th className="px-6 py-3 text-left font-medium text-gray-500">
                                            Descrição SPN
                                        </th>
                                        <th className="px-6 py-3 text-left font-medium text-gray-500">
                                            FMI
                                        </th>
                                        <th className="px-6 py-3 text-left font-medium text-gray-500">
                                            Descrição FMI
                                        </th>
                                        <th className="px-6 py-3 text-left font-medium text-gray-500">
                                            Status
                                        </th>
                                        <th className='table-action' />
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {loading ? <DtcAlertsSkeleton /> :
                                        dtcAlerts.map((alert) => (
                                            <>
                                                <tr key={alert.id} className={`hover:bg-gray-50 ${getStatusClass(alert.status)} ${(alert.alerts?.length ?? 0) > 1 ? 'compactRow' : ''}`}>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-gray-900">
                                                            {new Date(alert.alerts?.[alert.alerts.length - 1]?.dateTime ?? alert.dateTime).toLocaleString('pt-BR')}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="font-medium text-gray-900">
                                                            {alert.spn}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="text-gray-900 max-w-xs truncate" title={alert.spnDescription}>
                                                            {alert.spnDescription}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="font-medium text-gray-900">
                                                            {alert.fmi}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="text-gray-900 max-w-xs truncate" title={alert.fmiDescription}>
                                                            {alert.fmiDescription}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {getStatusBadge(getStatusClass(alert.status))}
                                                    </td>
                                                    <td className='table-action' onClick={() => setExpandedRows(expandedRows.includes(alert.id) ? expandedRows.filter(id => id !== alert.id) : [...expandedRows, alert.id])}>
                                                        {(alert.alerts?.length ?? 0) > 1 &&
                                                            <span>
                                                                <span className='btn btn-muted btn-icon-only'>
                                                                    <span className={`rioglyph ${expandedRows.includes(alert.id) ? 'rioglyph-chevron-up' : 'rioglyph-chevron-down'}`} />
                                                                </span>
                                                            </span>
                                                        }
                                                    </td>
                                                </tr>

                                                {expandedRows.includes(alert.id) &&
                                                    alert.alerts?.map((childAlert) => (
                                                        <tr className='extendedRow' key={childAlert.id}>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="text-gray-900">
                                                                    {new Date(childAlert.dateTime).toLocaleString('pt-BR')}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="font-medium text-gray-900">
                                                                    {childAlert.spn}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <div className="text-gray-900 max-w-xs truncate" title={childAlert.spnDescription}>
                                                                    {childAlert.spnDescription}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="font-medium text-gray-900">
                                                                    {childAlert.fmi}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <div className="text-gray-900 max-w-xs truncate" title={childAlert.fmiDescription}>
                                                                    {childAlert.fmiDescription}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                {getStatusBadge(getStatusClass(childAlert.status))}
                                                            </td>
                                                            <td className='table-action' />
                                                        </tr>
                                                    ))}
                                            </>
                                        ))}
                                </tbody>
                            </table>
                        </div>

                        {dtcAlerts.length === 0 && !loading && (
                            <div className="text-center py-12">
                                <div className="text-gray-500">
                                    <p className="font-medium mb-2">Nenhum alerta DTC encontrado</p>
                                    <p className="text-sm">Não há histórico de alertas para exibir.</p>
                                </div>
                            </div>
                        )}

                    </div>

                    {dtcAlerts.length > 0 && !loading && <LoadMoreButton
                        loaded={dtcAlerts.map(alert => alert.alerts ?? []).flat().length}
                        total={total}
                        onLoadMore={loadMore}
                        loadMoreMessage="Carregar mais"
                        noMoreMessage="Tudo carregado"
                    />}
                </div>}
        />
    </>
};